import { FC, useState } from 'react';
import { mutate } from "swr";
import { useRouter } from "next/router";
import { IconButton, Menu, MenuItem, MenuButton, MenuList, Grid, GridItem, useDisclosure } from "@chakra-ui/react";
import { isSharedPack } from "domains/packs/utils";
import { SharePackButton, SharePackModal } from "domains/interactions";
import { usePack, useToastNotification } from 'lib/hooks';
import { VerticalDotsIcon } from "lib/icons/verticalDots";
import { ActivePackProvider } from "lib/context";
import { DeletePackAlertDialog } from "./deletePackAlertDialog";
import { packsClient } from "lib/clients";
import { useE2EEContext } from 'lib/e2ee';
import { clientSideCopyPack, copyPack, templatizePack, clientSideTemplatizePack } from 'lib/utils';
import { usePackContext } from "../context";
import { isReadOnly, isAdmin } from "lib/utils";

export const PackActions: FC = () => {
  const { packId } = usePackContext();
  const { data: pack, isAnon } = usePack(packId);
  const sharePackModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const { errorToast, successToast } = useToastNotification();
  const router = useRouter();
  const { keys } = useE2EEContext();
  const [ processing, setProcessing ] = useState<boolean>(false);

  const handlePreview = (): void => {
    const hash = window.location.hash;

    window.open(`${window.location.pathname}/preview${hash}`, '_blank');
  }

  const deletePack = async (): Promise<void> => {
    try {
      deleteDialog.onClose();

      isSharedPack(pack.id)
        ? await packsClient.removeInvitedPack(pack.id)
        : await packsClient.deletePack(pack.id)

      await mutate('/v1/packs');
      await router.push('/packs');
    } catch (err) {
      errorToast({ title: 'Error', description: 'Could not delete pack' })
    }
  }

  const duplicatePack = async (): Promise<void> => {
    try {
      setProcessing(true);

      let data = null;
      
      if (pack.key) {
        data = await clientSideCopyPack(pack, keys);
      }
      else {
        data = await copyPack(pack);
      }

      await mutate('/v1/packs');
      successToast({ title: 'Success', description: 'Pack has been duplicated' });
      await router.push(`/packs/${data.id}`);
    } catch (err) {
      errorToast({ title: 'Error', description: 'Could not copy pack' });
    }
    finally {
      setProcessing(false);
    }
  }

  const onCreateTemplate = async(): Promise<void> => {
    try {
      setProcessing(true);

      if (pack.key) {
        await clientSideTemplatizePack(pack, keys);
      }
      else {
        await templatizePack(pack);
      }
      
      successToast({ title: 'Success', description: `A template has been created based on ${pack.title}` })
    } catch (e) {
      errorToast({ title: 'Error', description: `${pack.title} could not be templated, please try again` })
    }
    finally {
      setProcessing(false);
    }
  }

  return (
    <ActivePackProvider value={{ activePack: pack }}>
      <Grid gap="10px" gridAutoFlow="column" gridTemplateColumns={['auto 40px', '120px 40px']}>
        <GridItem>
          {!isAnon && isAdmin(pack) && <SharePackButton onOpen={sharePackModal.onOpen} />}
        </GridItem>
        <GridItem>
          <Menu autoSelect={false} placement="top-start">
            <MenuButton
              as={IconButton}
              aria-label="actions"
              icon={<VerticalDotsIcon fill="white" />}
              variant="primary"
              isLoading={processing}
            />
            <MenuList>
              {!isAnon && <MenuItem onClick={onCreateTemplate}>Templatize</MenuItem>}
              {!isReadOnly(pack) && <MenuItem onClick={handlePreview}>Preview pack</MenuItem>}
              {!isAnon && <MenuItem onClick={duplicatePack}>Duplicate pack</MenuItem>}
              {!isAnon && <MenuItem onClick={deleteDialog.onOpen}>{isSharedPack(pack?.id) ? 'Remove from my Packs' : 'Delete pack'}</MenuItem>}
            </MenuList>
          </Menu>
        </GridItem>
      </Grid>
      <SharePackModal isOpen={sharePackModal.isOpen} onClose={sharePackModal.onClose} />
      <DeletePackAlertDialog isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} onDelete={deletePack}/>
    </ActivePackProvider>
  )
};
